import { last, uniqBy } from "lodash-es";
import { getClientLogoByClientId } from "../../services/api/client.api";
import {
  getRoosterLinkData,
  getRoosterLinkMeetingData
} from "../../services/api/meeting.api";
import {
  IAvailabilityDocData,
  RoosterLinkAvailabilityIDResponse
} from "../../services/api/types/roostermeeting.type";
import {
  MeetingParticipantModelType,
  MeetingProfileType,
  SchedulerStoreType
} from "../../state/scheduler.store";
import { handleRoosterLinkValidationErrorMessageState } from "../../utils/errorManagement";
import { getLinkType } from "../link";
import moment from "moment-timezone";
import { v4 } from "uuid";
import { TFunction } from "i18next";
import { validateRoosterLinkHelper } from "./validate";

export const handleRoosterLink = async (
  actions: SchedulerStoreType["actions"],
  linkType: ReturnType<typeof getLinkType>,
  t: TFunction,
  requestSessionStartedAt = Date.now()
) => {
  if (!linkType?.linkId || !linkType?.userId) {
    return;
  }

  actions.setLink(
    linkType.userId,
    linkType.linkId,
    linkType.availabilityId ?? "",
    linkType.viewMode ?? null
  );

  const roosterLinkData = await getRoosterLinkData({
    linkId: linkType.linkId,
    userId: linkType.userId
  });

  if (!roosterLinkData) {
    const { hasError, error } = await validateRoosterLinkHelper({
      personIdentifier: linkType.userId,
      profileIdentifier: linkType.linkId,
      userUid: "",
      link: {
        scheduleId: "",
        viewMode: linkType.viewMode,
        userId: linkType.userId,
        linkId: linkType.linkId
      }
    });

    await handleRoosterLinkValidationErrorMessageState(
      hasError,
      false,
      error,
      actions,
      {
        currentView: "month",
        currentStep: "initial"
      },
      undefined,
      false,
      t
    );
    return;
  }

  const logoUrl = await getClientLogoByClientId(roosterLinkData.clientId);
  actions.setClientLogo(logoUrl);

  let availabilityDoc: RoosterLinkAvailabilityIDResponse | null | undefined =
    null;

  if (
    linkType.availabilityId &&
    roosterLinkData?.id &&
    roosterLinkData?.userId
  ) {
    availabilityDoc = await getRoosterLinkMeetingData({
      linkId: linkType.linkId,
      roosterLinkId: roosterLinkData?.id,
      internalUserId: roosterLinkData?.userId,
      userId: linkType.userId,
      availabilityId: linkType.availabilityId
    });
  }

  if (!availabilityDoc) {
    const url = new URL(window.location.href);
    url.searchParams.delete("id");
    window.history.replaceState({}, document.title, url.toString());
  }

  if (roosterLinkData?.clientId) {
    if (linkType.availabilityId && !linkType.viewMode) {
      actions.setUiState({
        currentView: "month",
        currentStep: "scheduled"
      });
    }
    const isStandalone = roosterLinkData?.isStandalone ?? false;
    actions.setClient(
      isStandalone ? "standalone" : "normal",
      roosterLinkData?.clientId
    );
    const organizer = isStandalone
      ? roosterLinkData.organizer
      : {
          email: roosterLinkData?.email ?? "",
          name: roosterLinkData?.hostName ?? "",
          value: roosterLinkData?.email ?? ""
        };

    const allAttendees = uniqBy(
      [...(roosterLinkData?.interviewers ?? []), organizer],
      "email"
    );

    if (
      organizer?.name &&
      organizer?.email &&
      roosterLinkData?.availability &&
      roosterLinkData?.additionalPreferences
    ) {
      actions.setHost(organizer?.name, organizer.email, {
        availability: roosterLinkData.availability,
        additionalPreferences: roosterLinkData.additionalPreferences
      });
    }
    actions.setMeetingLink({
      id: roosterLinkData.id,
      type: "rooster_links",
      mode: "autobook",
      roosterLinksMeta: {
        email: roosterLinkData.email ?? "",
        title: roosterLinkData?.meetingProfileName,
        description: roosterLinkData?.meetingDescription,
        organizer: roosterLinkData.organizer,
        profileType: roosterLinkData?.profileType as MeetingProfileType,
        participantType:
          roosterLinkData?.meetingType as MeetingParticipantModelType,
        candidateRequiredFields:
          roosterLinkData?.candidateRequiredFields as string[],
        termsAndConditions: []
      },
      interviewers: allAttendees,
      availability: {
        type: "range",
        availabilityWindowMins:
          (roosterLinkData?.meetingAvailabilityWindow ?? 1) * 7 * 1440,
        range: [
          {
            rangeStart: moment().startOf("day").format(),
            rangeEnd: moment()
              .startOf("day")
              .add(
                roosterLinkData?.meetingAvailabilityWindow * 7 * 1440,
                "minutes"
              )
              .format()
          }
        ],
        duration: roosterLinkData?.meetingDurationInMins,
        autobookIntervalFreq: 30
      },
      isDisabled: roosterLinkData?.disabled,
      canCancel: availabilityDoc?.docData.cancel ?? roosterLinkData?.canCancel,
      canReschedule:
        availabilityDoc?.docData.reschedule ?? roosterLinkData?.canReschedule,
      showTentativeAsBusy: roosterLinkData?.showTentativeAsBusy,
      scheduledData: {
        form: availabilityDoc?.formData ?? {},
        docDataRoosterLinks: {
          clientId:
            availabilityDoc?.docData.clientId ?? roosterLinkData?.clientId,
          customUrl: false,
          schedule: true,
          cancel:
            availabilityDoc?.docData?.cancel ?? roosterLinkData?.canCancel,
          reschedule:
            availabilityDoc?.docData?.reschedule ??
            roosterLinkData?.canReschedule,
          days: availabilityDoc?.docData.days ?? [],
          duration:
            availabilityDoc?.docData.duration ??
            roosterLinkData?.meetingDurationInMins,
          interviewers:
            availabilityDoc?.docData.interviewers ??
            roosterLinkData?.interviewers.map((i) => i.email),
          minutesBefore: availabilityDoc?.docData.minutesBefore ?? 0,
          minutesAfter: availabilityDoc?.docData.minutesAfter ?? 0,
          parent: availabilityDoc?.docData.parent ?? v4(),
          start: availabilityDoc?.docData.start ?? moment().valueOf(),
          end: availabilityDoc?.docData.end ?? moment().valueOf(),
          timeZone: availabilityDoc?.docData.timeZone ?? moment.tz.guess(),
          workerId:
            availabilityDoc?.docData.workerId ?? roosterLinkData?.workerId,
          conferenceType:
            availabilityDoc?.docData?.conferenceType ??
            roosterLinkData?.conferenceType ??
            [],
          candidateEmailTemplate:
            availabilityDoc?.docData.candidateEmailTemplate ??
            roosterLinkData?.candidateEmailTemplate,
          interviewerEmailTemplate:
            availabilityDoc?.docData.interviewerEmailTemplate ??
            roosterLinkData?.interviewerEmailTemplate,
          organizer:
            availabilityDoc?.docData?.organizer ?? roosterLinkData?.organizer,
          additionalInterviewersInfo:
            roosterLinkData?.interviewers ??
            availabilityDoc?.docData?.additionalInterviewersInfo ??
            [],
          profileType:
            availabilityDoc?.docData.profileType ??
            roosterLinkData?.profileType,
          userId: availabilityDoc?.docData.userId ?? roosterLinkData?.userId,
          id: availabilityDoc?.docData.id ?? roosterLinkData?.id,
          client: availabilityDoc?.docData.client ?? roosterLinkData?.clientId,
          pmlInitiatedBy: availabilityDoc?.docData.pmlInitiatedBy ?? "",
          pmlProfileId: availabilityDoc?.docData.pmlProfileId ?? "",
          created: availabilityDoc?.docData.created ?? "",
          updated: availabilityDoc?.docData.updated ?? "",
          selected: availabilityDoc?.docData.selected ?? [],
          candidate: availabilityDoc?.docData?.candidate,
          cancelled: availabilityDoc?.docData?.cancelled ?? false
        },
        availabilityDocData: availabilityDoc?.availabilityDocData as
          | IAvailabilityDocData
          | undefined,
        alreadyScheduled: availabilityDoc?.alreadyScheduled ?? false
      }
    });
  }
  if (roosterLinkData?.providers) {
    actions.setProviders(roosterLinkData?.providers ?? []);
  }

  const prevSelectedSlot = availabilityDoc?.docData.selected;
  const isRescheduleFlow = !!(prevSelectedSlot && prevSelectedSlot?.length > 0);
  const isCancelled = availabilityDoc?.docData?.cancelled ?? false;
  actions.setSessionData({
    isRescheduleFlow: !!(prevSelectedSlot && prevSelectedSlot?.length > 0),
    requestSessionStartedAt,
    selectedTimezone: last(prevSelectedSlot)?.timeZone ?? moment.tz.guess(),
    selectedSlot: null,
    prevSelectedSlot,
    computedAvailability: [],
    formData: availabilityDoc?.formData ?? {}
  });

  if (
    roosterLinkData?.userId &&
    roosterLinkData?.workerId &&
    roosterLinkData?.username
  ) {
    actions.setUser(
      roosterLinkData?.userId,
      roosterLinkData?.username,
      roosterLinkData?.workerId
    );
  }

  if (linkType.viewMode === "initial") {
    actions.setUiState({
      currentView: "month",
      currentStep: "initial"
    });
    return;
  }

  actions.setUiState({
    currentView: "month",
    currentStep:
      isRescheduleFlow && !isCancelled
        ? "scheduled"
        : isCancelled
          ? "scheduling_cancelled"
          : "initial"
  });
};
